// LoanDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  LinearProgress,
  Tooltip,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  DialogContentText,
  Alert,
} from "@mui/material";

import LoadingSpinner from "../components/LoadingSpinner";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";

import {
  getLoans,
  createLoan,
  processLoanApproval,
  processWaiverApproval,
  requestPenaltyWaiver,
  resendLoanOtp,
  verifyLoanOtp,
} from "../services/loanService";
import { fetchSystemSettings } from "../services/systemSettingsService";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { useAuth } from "../contexts/AuthContext";
import { fetchGroupTerms } from "../services/groupService";
import LoanOtpDialog from "../components/LoanOtpDialog";
const brand = "#800b33";
const brandHover = "#6c0a2b";

const currencyFmt = (v) =>
  new Intl.NumberFormat("en-TZ", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

const fadeIn = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.35, ease: "easeOut" },
  }),
};

const loanStatusChipProps = (status, t) => {
  switch (status) {
    case "PENDING_CHAIR_APPROVAL":
      return { label: t("Pending Chair"), color: "warning" };
    case "PENDING_SECRETARY_APPROVAL":
      return { label: t("Pending Secretary"), color: "warning" };
    case "PENDING_ACCOUNTANT_APPROVAL":
      return { label: t("Pending Accountant"), color: "warning" };
    case "APPROVED_FOR_DISBURSEMENT":
      return { label: t("Waiting Disbursement"), color: "info" };
    case "ACTIVE":
      return { label: t("Active"), color: "success" };
    case "CLOSED":
      return { label: t("Closed"), color: "default" };
    case "REJECTED":
      return { label: t("Rejected"), color: "error" };
    default:
      return { label: t(status), color: "default" };
  }
};

const extractLoanApiMessage = (error) => {
  if (error?.payload?.detail) return error.payload.detail;
  const raw = error?.message || "Unexpected loan service error.";
  return raw.replace(/^API request failed:\s*/, "");
};

const termTypeLabel = (term, t) => {
  switch (term) {
    case "DAILY":
      return t("Daily");
    case "WEEKLY":
      return t("Weekly");
    case "MONTHLY":
      return t("Monthly");
    default:
      return t(term);
  }
};

// ---------------- MAIN COMPONENT ----------------

export default function LoanApprovals() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [loans, setLoans] = useState([]);
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("all"); // all | mine
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateLoanOpen, setCreateLoanOpen] = useState(false);
  const [isWaiverDialogOpen, setWaiverDialogOpen] = useState(false);
  const [terms, setTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState("current");

  // State for waiver dialog
  const [waiverForm, setWaiverForm] = useState({
    reason: "",
    start: "",
    end: "",
  });

  // State for the new loan dialog
  const [loanForm, setLoanForm] = useState({
    principal: "",
    termMonths: "",
    termType: "MONTHLY",
  });
  const [loanFormErrors, setLoanFormErrors] = useState({});
  const [maxLoanTermMonths, setMaxLoanTermMonths] = useState(12);
  const [loanOtpState, setLoanOtpState] = useState(null);
  const [loanOtpError, setLoanOtpError] = useState("");
  const [isVerifyingLoanOtp, setIsVerifyingLoanOtp] = useState(false);
  const [isResendingLoanOtp, setIsResendingLoanOtp] = useState(false);
  const [isStartingLoanOtp, setIsStartingLoanOtp] = useState(false);

  useEffect(() => {
    let active = true;
    fetchGroupTerms()
      .then((payload) => {
        if (!active) return;
        const list = Array.isArray(payload) ? payload : payload?.results || payload?.data || [];
        setTerms(list);
      })
      .catch((err) => console.error("Failed to load terms:", err));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    fetchSystemSettings()
      .then((payload) => {
        if (!active) return;
        const maxTerm = Number(payload?.max_loan_term_months);
        if (!Number.isNaN(maxTerm) && maxTerm > 0) {
          setMaxLoanTermMonths(maxTerm);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const activeTermId = useMemo(
    () => terms.find((term) => term.status === "ACTIVE")?.id || null,
    [terms]
  );
  const effectiveTermId = useMemo(() => {
    if (selectedTermId === "all") return null;
    if (selectedTermId === "current") return activeTermId || terms[0]?.id || null;
    return selectedTermId;
  }, [selectedTermId, activeTermId, terms]);

  // Fetch loans from API on component mount
  useEffect(() => {
    if (selectedTermId === "current" && !effectiveTermId) {
      return undefined;
    }
    const fetchLoans = async () => {
      setIsLoading(true);
      try {
        const termFilter = effectiveTermId ? { term_id: effectiveTermId } : {};
        const response = await getLoans(termFilter);
        // Ensure that we always have an array.
        const loansData = Array.isArray(response) ? response : response?.results || [];
        setLoans(loansData);
        if (loansData.length > 0) {
          setSelectedLoanId(loansData[0].id);
        }
      } catch (error) {
        Swal.fire(
          t("Error"),
          t("Failed to fetch loans: {{message}}", { message: error.message }),
          "error"
        );
        setLoans([]); // On error, reset to an empty array to prevent crashes.
      }
      setIsLoading(false);
    };
    fetchLoans();
  }, [effectiveTermId, selectedTermId, t]);

  const handleScopeChange = (_, val) => {
    if (val) setScope(val);
  };

  const handleStatusFilterChange = (_, val) => {
    if (val) setStatusFilter(val);
  };

  const filteredLoans = useMemo(() => {
    let list = [...loans];

    if (scope === "mine") {
      list = list.filter((l) => l.member === user?.member_id);
    }

    if (statusFilter !== "ALL") {
      list = list.filter((l) => l.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (l) =>
          l.id.toLowerCase().includes(q) ||
          l.member_name.toLowerCase().includes(q) ||
          String(l.principal_amount).includes(q)
      );
    }
    return list;
  }, [loans, scope, statusFilter, search, user]);

  const selectedLoan = useMemo(
    () => filteredLoans.find((l) => l.id === selectedLoanId) || filteredLoans[0] || null,
    [filteredLoans, selectedLoanId]
  );

  // Summary stats
  const totalActive = filteredLoans.filter((l) => l.status === "ACTIVE").length;
  const totalOutstanding = filteredLoans
    .filter((l) => ["ACTIVE", "PENDING_ACCOUNTANT_APPROVAL"].includes(l.status))
    .reduce((sum, l) => sum + Number(l.outstanding_amount || 0), 0);
  const totalPenalties = filteredLoans.reduce(
    (sum, l) => sum + Number(l.total_penalty || 0),
    0
  );

  // ------- API-driven Actions -------

  const refreshLoans = async () => {
    try {
      const termFilter = effectiveTermId ? { term_id: effectiveTermId } : {};
      const data = await getLoans(termFilter);
      setLoans(data);
    } catch (error) {
      console.error("Failed to refresh loans:", error);
      Swal.fire(
        t("Error"),
        t("Failed to refresh loans: {{message}}", { message: error.message }),
        "error"
      );
    }
  };

  const handleLoanApprovalAction = async (isApprove) => {
    if (!selectedLoan) return;

    const role = user?.role; // Using optional chaining
    const status = selectedLoan.status;

    const isLeadership = ["Mwenyekiti", "Katibu", "Muhasibu"].includes(role);
    if (!isLeadership) {
      Swal.fire({
        icon: "error",
        title: t("Not Allowed"),
        text: t("Only leadership can approve or reject loans."),
        confirmButtonColor: brand,
      });
      return;
    }

    const result = await Swal.fire({
      icon: isApprove ? "question" : "warning",
      title: isApprove ? t("Approve Loan?") : t("Reject Loan?"),
      text: isApprove
        ? t("This will move the loan to the next stage.")
        : t("This will mark the loan as rejected."),
      input: !isApprove ? "text" : undefined,
      inputLabel: !isApprove ? t("Reason (required)") : undefined,
      inputValidator: (v) =>
        !isApprove && !v ? t("Rejection reason is required.") : undefined,
      showCancelButton: true,
      confirmButtonText: isApprove ? t("Approve") : t("Reject"),
      confirmButtonColor: isApprove ? brand : "#c62828",
    });

    if (!result.isConfirmed) return;

    try {
      const action = isApprove ? "approve" : "reject";
      const actionLabel = isApprove ? t("approved") : t("rejected");
      await processLoanApproval(selectedLoan.id, action, result.value);
      await refreshLoans(); // Refresh data from server
      Swal.fire(
        t("Success"),
        t("Loan has been {{status}}.", { status: actionLabel }),
        "success"
      );
    } catch (error) {
      Swal.fire(
        t("Error"),
        t("Action failed: {{message}}", { message: error.message }),
        "error"
      );
    }
  };

  const handleRepayLoan = async () => {
    if (!selectedLoan) return;

    if (!["ACTIVE"].includes(selectedLoan.status)) {
      Swal.fire({
        icon: "info",
        title: t("Loan Not Active"),
        text: t("Repayment is allowed only when loan is ACTIVE."),
        confirmButtonColor: brand,
      });
      return;
    }

    const { value: amount } = await Swal.fire({
      icon: "question",
      title: t("Repay Loan"),
      input: "number",
      inputLabel: t("Amount (TZS)"),
      inputPlaceholder: t("e.g. 20000"),
      inputValidator: (v) =>
        !v || Number(v) <= 0
          ? t("Amount must be greater than zero.")
          : undefined,
      showCancelButton: true,
      confirmButtonText: t("Submit"),
      confirmButtonColor: brand,
    });

    if (!amount) return;

    const pay = Number(amount);

    // TODO: Implement `repayLoan` in loanService.js and call it here
    console.log(`Repaying ${pay} for loan ${selectedLoan.id}`);
    Swal.fire(
      t("Info"),
      t("Repayment functionality is not yet connected to the API."),
      "info"
    );
    // await repayLoan(selectedLoan.id, pay);
    // await refreshLoans();
  };

  const handleWaiverApprovalAction = async (waiver, isApprove) => {
    const role = user?.role; // Using optional chaining
    const isLeadership = ["Mwenyekiti", "Katibu", "Muhasibu"].includes(role);
    if (!isLeadership) {
      Swal.fire({
        icon: "error",
        title: t("Not Allowed"),
        text: t("Only leadership can approve penalty waivers."),
        confirmButtonColor: brand,
      });
      return;
    }

    const result = await Swal.fire({
      icon: isApprove ? "question" : "warning",
      title: isApprove ? t("Approve Waiver?") : t("Reject Waiver?"),
      text: isApprove
        ? t("No penalty will be applied for the specified duration.")
        : t("This will reject the waiver request."),
      input: !isApprove ? "text" : undefined,
      inputLabel: !isApprove ? t("Reason (required)") : undefined,
      inputValidator: (v) =>
        !isApprove && !v ? t("Rejection reason is required.") : undefined,
      showCancelButton: true,
      confirmButtonText: isApprove ? t("Approve") : t("Reject"),
      confirmButtonColor: isApprove ? brand : "#c62828",
    });

    if (!result.isConfirmed) return;

    try {
      const action = isApprove ? t("approved") : t("rejected");
      const reason = isApprove ? "" : result.value;
      await processWaiverApproval(waiver.id, reason);
      await refreshLoans();
      Swal.fire(
        t("Success"),
        t("Waiver request has been {{status}}.", { status: action }),
        "success"
      );
    } catch (error) {
      Swal.fire(
        t("Error"),
        t("Action failed: {{message}}", { message: error.message }),
        "error"
      );
    }
  };

  const handleOpenCreateLoanDialog = () => {
    setLoanForm({ principal: "", termMonths: "", termType: "MONTHLY" });
    setLoanFormErrors({});
    setLoanOtpError("");
    setCreateLoanOpen(true);
  };

  const handleCloseCreateLoanDialog = () => {
    if (isStartingLoanOtp) return;
    setCreateLoanOpen(false);
  };

  const handleCloseLoanOtpDialog = () => {
    setLoanOtpState(null);
    setLoanOtpError("");
    setIsVerifyingLoanOtp(false);
    setIsResendingLoanOtp(false);
  };

  const handleCreateLoanSubmit = async () => {
    const errors = {};
    const principal = Number(loanForm.principal);
    const termMonths = Number(loanForm.termMonths);

    if (principal <= 0 || termMonths <= 0) {
      errors.form = t("Principal and term must be greater than zero.");
    }
    if (termMonths > maxLoanTermMonths) {
      errors.termMonths = t("Loan term cannot exceed {{max}} months.", { max: maxLoanTermMonths });
    }

    if (Object.keys(errors).length > 0) {
      setLoanFormErrors(errors);
      return;
    }
    setLoanFormErrors({});
    setLoanOtpError("");
    setIsStartingLoanOtp(true);

    try {
      const newLoanData = {
        principal_amount: principal,
        term_months: termMonths,
        term_type: loanForm.termType,
      };
      const pendingResponse = await createLoan(newLoanData);
      setCreateLoanOpen(false);
      window.setTimeout(() => {
        setLoanOtpState(pendingResponse);
      }, 150);
    } catch (error) {
      Swal.fire(
        t("Error"),
        t("Failed to start loan request: {{message}}", { message: extractLoanApiMessage(error) }),
        "error"
      );
    } finally {
      setIsStartingLoanOtp(false);
    }
  };

  const handleVerifyLoanOtp = async (otpCode) => {
    if (!loanOtpState?.pending_request_id) return;
    if (!otpCode || otpCode.length !== 6) {
      setLoanOtpError(t("Enter the 6-digit OTP sent to your registered contact(s)."));
      return;
    }

    setIsVerifyingLoanOtp(true);
    setLoanOtpError("");
    try {
      const response = await verifyLoanOtp({
        pending_request_id: loanOtpState.pending_request_id,
        otp_code: otpCode,
      });
      await refreshLoans();
      if (response?.loan?.id) {
        setSelectedLoanId(response.loan.id);
      }
      handleCloseLoanOtpDialog();
      Swal.fire(t("Success"), t("Loan request verified and submitted successfully."), "success");
    } catch (error) {
      const payload = error?.payload || {};
      if (payload.attempts_left !== undefined) {
        setLoanOtpState((prev) => (
          prev ? { ...prev, attempts_left: Number(payload.attempts_left) } : prev
        ));
      }
      setLoanOtpError(extractLoanApiMessage(error));
    } finally {
      setIsVerifyingLoanOtp(false);
    }
  };

  const handleResendLoanOtp = async () => {
    if (!loanOtpState?.pending_request_id) return;
    setIsResendingLoanOtp(true);
    setLoanOtpError("");
    try {
      const response = await resendLoanOtp({ pending_request_id: loanOtpState.pending_request_id });
      setLoanOtpState(response);
    } catch (error) {
      const payload = error?.payload || {};
      setLoanOtpState((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          attempts_left:
            payload.attempts_left !== undefined
              ? Number(payload.attempts_left)
              : prev.attempts_left,
        };
      });
      setLoanOtpError(extractLoanApiMessage(error));
    } finally {
      setIsResendingLoanOtp(false);
    }
  };

  const handleOpenWaiverDialog = () => {
    if (!user || selectedLoan.member !== user.member_id) {
      Swal.fire({
        icon: "error",
        title: t("Not Allowed"),
        text: t("Only the borrower can request a penalty waiver."),
        confirmButtonColor: brand,
      });
      return;
    }
    setWaiverForm({ reason: "", start: "", end: "" });
    setWaiverDialogOpen(true);
  };

  const handleCloseWaiverDialog = () => {
    setWaiverDialogOpen(false);
  };

  const handleWaiverSubmit = async () => {
    const { reason, start, end } = waiverForm;
    const today = new Date().toISOString().slice(0, 10);
    if (!reason || !start || !end) {
      Swal.fire({
        icon: "warning",
        title: t("Missing Fields"),
        text: t("Reason, start date, and end date are all required."),
        confirmButtonColor: brand,
      });
      return;
    }
    if (start < today) {
      Swal.fire({
        icon: "error",
        title: t("Invalid Date"),
        text: t("Start date cannot be in the past."),
        confirmButtonColor: brand,
      });
      return;
    }
    if (end < start) {
      Swal.fire({
        icon: "error",
        title: t("Invalid Date Range"),
        text: t("End date cannot be earlier than start date."),
        confirmButtonColor: brand,
      });
      return;
    }

    try {
      const waiverData = {
        reason,
        start_date: start,
        end_date: end,
        member: selectedLoan?.member,
        loan: selectedLoan?.id,
      };
      await requestPenaltyWaiver(waiverData);
      await refreshLoans();
      handleCloseWaiverDialog();
      Swal.fire(t("Success"), t("Waiver request submitted."), "success");
    } catch (error) {
      Swal.fire(
        t("Error"),
        t("Failed to submit waiver request: {{message}}", {
          message: error.message,
        }),
        "error"
      );
    }
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner />;
  }

  // ---------------- RENDER ----------------

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
            {t("Loan Management")}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {t(
              "View loan portfolio, approvals, repayments and penalty waivers for your Savvy Community/SACCOS group."
            )}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            value={scope}
            exclusive
            size="small"
            onChange={handleScopeChange}
          >
            <ToggleButton value="all">{t("All Loans")}</ToggleButton>
            <ToggleButton value="mine">{t("My Loans")}</ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            size="small"
            onChange={handleStatusFilterChange}
          >
            <ToggleButton value="ALL">{t("All Status")}</ToggleButton>
            <ToggleButton value="ACTIVE">{t("Active")}</ToggleButton>
            <ToggleButton value="PENDING_CHAIR_APPROVAL">{t("Pending")}</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            select
            size="small"
            label={t("Term")}
            value={selectedTermId}
            onChange={(e) => setSelectedTermId(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="current">{t("Current Term")}</MenuItem>
            <MenuItem value="all">{t("All Terms")}</MenuItem>
            {terms.map((term) => (
              <MenuItem key={term.id} value={term.id}>
                {term.name || `Term ${term.term_number}`}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            sx={{ minWidth: 230 }}
            placeholder={t("Search loan or member...")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            startIcon={<SavingsOutlinedIcon />}
            onClick={handleOpenCreateLoanDialog}
            sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
          >
            {t("Apply Loan")}
          </Button>
        </Stack>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TimelineIcon sx={{ color: brand }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("Active Loans")}
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {totalActive}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} md={4}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <PaidOutlinedIcon sx={{ color: brand }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("Total Outstanding")}
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {currencyFmt(totalOutstanding)} TZS
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} md={4}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <ErrorOutlineOutlinedIcon sx={{ color: brand }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("Accumulated Penalties")}
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {currencyFmt(totalPenalties)} TZS
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Main Layout */}
      <Grid container spacing={2}>
        {/* Left: Loans table */}
        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  p: 2,
                  pb: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6" fontWeight={800}>
                  {t("Loans")}
                </Typography>
                <Tooltip title={t("Status is based on multi-level approval and repayment progress.")}>
                  <InfoOutlinedIcon sx={{ color: "text.secondary" }} />
                </Tooltip>
              </Box>
              <Divider />
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("Loan")}</TableCell>
                      <TableCell>{t("Borrower")}</TableCell>
                      <TableCell align="right">{t("Outstanding")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <LinearProgress />
                        </TableCell>
                      </TableRow>
                    )}
                    {!isLoading && filteredLoans.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 4, color: "text.secondary" }}
                        >
                          {t("No loans found.")}
                        </TableCell>
                      </TableRow>
                    )}
                    {!isLoading &&
                      filteredLoans.map((l) => {
                        const chip = loanStatusChipProps(l.status, t);
                        return (
                          <TableRow
                            key={l.id}
                            hover
                            selected={l.id === selectedLoanId}
                            onClick={() => setSelectedLoanId(l.id)}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={700}>
                                {l.id}
                              </Typography>
                              <Chip
                                size="small"
                                label={chip.label}
                                color={chip.color}
                                sx={{ mt: 0.5 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <PersonOutlineOutlinedIcon
                                  fontSize="small"
                                  sx={{ color: "text.secondary" }}
                                />
                                <Typography variant="body2">
                                  {l.member_name}
                                </Typography>
                              </Stack>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {termTypeLabel(l.term_type, t)} •{" "}
                                {t("{{count}} month(s)", { count: l.term_months })}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                color="text.primary"
                              >
                                {currencyFmt(l.outstanding_amount)} TZS
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {t("Principal {{amount}}", {
                                  amount: currencyFmt(l.principal_amount),
                                })}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Loan details */}
        <Grid item xs={12} md={7}>
          {selectedLoan ? (
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                {/* Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={900}>
                      {t("Loan {{id}}", { id: selectedLoan.id })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("Borrower:")}{" "}
                      <b>
                        {selectedLoan.member_name} (#{selectedLoan.member})
                      </b>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("Created: {{date}} • Term: {{months}} month(s) • {{termType}}", {
                        date: selectedLoan.created_at || "-",
                        months: selectedLoan.term_months,
                        termType: termTypeLabel(selectedLoan.term_type, t),
                      })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("Start: {{start}} → End: {{end}}", {
                        start: selectedLoan.start_date || "-",
                        end: selectedLoan.end_date || "-",
                      })}
                    </Typography>
                  </Box>
                  <Stack spacing={1} alignItems="flex-end">
                    <Chip
                      {...loanStatusChipProps(selectedLoan.status, t)}
                      size="small"
                    />
                    <Chip
                      icon={<MonetizationOnOutlinedIcon />}
                      size="small"
                      label={t("Penalty: {{amount}} TZS", {
                        amount: currencyFmt(selectedLoan.total_penalty),
                      })}
                      variant="outlined"
                    />
                  </Stack>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Financial summary */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                      >
                        {t("Principal")}
                      </Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {currencyFmt(selectedLoan.principal_amount)} TZS
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("Interest: {{amount}}", {
                          amount: currencyFmt(selectedLoan.interest_amount),
                        })}{" "}
                        • {t("Charge: {{amount}}", { amount: currencyFmt(selectedLoan.df_amount) })}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                      >
                        {t("Total Payable")}
                      </Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {currencyFmt(selectedLoan.total_payable)} TZS
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("Penalty so far: {{amount}} TZS", {
                          amount: currencyFmt(selectedLoan.total_penalty),
                        })}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                      >
                        {t("Collected / Outstanding")}
                      </Typography>
                      <Typography variant="h6" fontWeight={900}>
                        {currencyFmt(selectedLoan.total_paid)} /{" "}
                        {currencyFmt(selectedLoan.outstanding_amount)} TZS
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("Status: {{label}}", {
                          label: loanStatusChipProps(selectedLoan.status, t).label,
                        })}
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>

                {/* Installments */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle1" fontWeight={800}>
                        {t("Installments & Penalties")}
                      </Typography>
                      <Tooltip title={t("Penalties are applied daily at 19:01 on unpaid installments.")}>
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "text.secondary" }}
                        />
                      </Tooltip>
                    </Stack>
                  </Box>

                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>{t("Due Date")}</TableCell>
                          <TableCell align="right">{t("Amount Due")}</TableCell>
                          <TableCell align="right">{t("Penalty")}</TableCell>
                          <TableCell align="right">{t("Paid")}</TableCell>
                          <TableCell align="right">{t("Balance")}</TableCell>
                          <TableCell>{t("Status")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedLoan.installments || []).length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              align="center"
                              sx={{ py: 3, color: "text.secondary" }}
                            >
                              {t("No installment schedule generated yet.")}
                            </TableCell>
                          </TableRow>
                        ) : (
                          selectedLoan.installments.map((inst, idx) => {
                            const totalDue =
                              Number(inst.amount_due || 0) +
                              Number(inst.penalty_amount || 0);
                            const paid = Number(inst.amount_paid || 0);
                            const bal = Math.max(totalDue - paid, 0);
                            return (
                              <TableRow key={inst.id} hover>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>{inst.due_date}</TableCell>
                                <TableCell align="right">
                                  {currencyFmt(inst.amount_due)}
                                </TableCell>
                                <TableCell align="right">
                                  {currencyFmt(inst.penalty_amount)}
                                </TableCell>
                                <TableCell align="right">
                                  {currencyFmt(inst.amount_paid)}
                                </TableCell>
                                <TableCell align="right">
                                  {currencyFmt(bal)}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={t(inst.status)}
                                    color={
                                      inst.status === "PAID"
                                        ? "success"
                                        : inst.status === "PARTIAL"
                                          ? "warning"
                                          : "default"
                                    }
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Penalty Waivers */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle1" fontWeight={800}>
                        {t("Penalty Waiver Requests")}
                      </Typography>
                      <Tooltip title={t("Members can request waiver; Chair/Katibu/Muhasibu can approve or reject.")}>
                        <InfoOutlinedIcon
                          fontSize="small"
                          sx={{ color: "text.secondary" }}
                        />
                      </Tooltip>
                    </Stack>
                    {selectedLoan.member === user?.member_id && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<GavelOutlinedIcon />}
                        sx={{
                          borderColor: brand,
                          color: brand,
                          "&:hover": { borderColor: brandHover, color: brandHover },
                        }}
                        onClick={handleOpenWaiverDialog}
                      >
                        {t("Request Waiver")}
                      </Button>
                    )}
                  </Box>

                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("Period")}</TableCell>
                          <TableCell>{t("Reason")}</TableCell>
                          <TableCell>{t("Status")}</TableCell>
                          {["Mwenyekiti", "Katibu", "Muhasibu"].includes(
                            user?.role
                          ) && <TableCell align="right">{t("Actions")}</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(selectedLoan.penalty_waivers || []).length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              align="center"
                              sx={{ py: 3, color: "text.secondary" }}
                            >
                              {t("No waiver requests.")}
                            </TableCell>
                          </TableRow>
                        ) : (
                          selectedLoan.penalty_waivers.map((w) => (
                            <TableRow key={w.id} hover>
                              <TableCell>
                                {w.start_date} → {w.end_date}
                              </TableCell>
                              <TableCell>{w.reason}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={t(w.status)}
                                  color={
                                    w.status === "APPROVED"
                                      ? "success"
                                      : w.status === "REJECTED"
                                        ? "error"
                                        : "warning"
                                  }
                                />
                              </TableCell>
                              <TableCell align="right">
                                {user && ["Mwenyekiti", "Katibu", "Muhasibu"].includes(
                                  user.role
                                ) &&
                                  w.status === "PENDING" && (
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      justifyContent="flex-end"
                                    >
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() =>
                                          handleWaiverApprovalAction(w, true)
                                        }
                                      >
                                        <DoneAllOutlinedIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          handleWaiverApprovalAction(w, false)
                                        }
                                      >
                                        <CloseOutlinedIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Bottom actions */}
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Grid item xs={12} md={7}>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        "All approvals, disbursements, repayments, penalties and waivers must be traceable in the audit trail and member statements, following SACCOs & microfinance best practices."
                      )}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={5}
                    display="flex"
                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    gap={1}
                  >
                    {/* Leadership: approve / reject */}
                    {user && ["Mwenyekiti", "Katibu", "Muhasibu"].includes(user.role) &&
                      selectedLoan.status.startsWith("PENDING_") && (
                        <>
                          <Button
                            variant="outlined"
                            color="success"
                            startIcon={<DoneAllOutlinedIcon />}
                            onClick={() => handleLoanApprovalAction(true)}
                          >
                            {t("Approve")}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<CloseOutlinedIcon />}
                            onClick={() => handleLoanApprovalAction(false)}
                          >
                            {t("Reject")}
                          </Button>
                        </>
                      )}

                    {/* Borrower: repay */}
                    {/* {selectedLoan.member === user?.member_id &&
                      selectedLoan.status === "ACTIVE" && (
                        <Button
                          variant="contained"
                          startIcon={<PaymentOutlinedIcon />}
                          onClick={handleRepayLoan}
                          sx={{
                            bgcolor: brand,
                            "&:hover": { bgcolor: brandHover },
                          }}
                        >
                          Repay Loan
                        </Button>
                      )} */}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ) : (
            <Typography color="text.secondary">
              {t("Select a loan from the left to view details.")}
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Create Loan Dialog */}
      <Dialog
        open={isCreateLoanOpen}
        onClose={handleCloseCreateLoanDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: brand, fontWeight: 800 }}>
          {t("Apply for a New Loan")}
        </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined">
                  {t("Your loan request will only be created after you verify the OTP sent to your registered contact(s).") }
                </Alert>
              </Grid>
              {loanFormErrors.form && (
                <Grid item xs={12}>
                  <Alert severity="warning">{loanFormErrors.form}</Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  label={t("Principal Amount (TZS)")}
                  fullWidth
                  value={
                  loanForm.principal
                    ? Number(loanForm.principal).toLocaleString("en-US")
                    : ""
                }
                onChange={(e) => {
                  const val = e.target.value.replace(/,/g, "");
                  if (!isNaN(val)) {
                    setLoanForm({ ...loanForm, principal: val });
                    if (loanFormErrors.form) setLoanFormErrors((prev) => ({ ...prev, form: "" }));
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">TZS</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Loan Term (months)")}
                fullWidth
                type="number"
                value={loanForm.termMonths}
                onChange={(e) => {
                  setLoanForm({ ...loanForm, termMonths: e.target.value });
                  if (loanFormErrors.termMonths || loanFormErrors.form) {
                    setLoanFormErrors((prev) => ({
                      ...prev,
                      termMonths: "",
                      form: "",
                    }));
                  }
                }}
                inputProps={{ min: 1, max: maxLoanTermMonths }}
                helperText={t("Max {{max}} months", { max: maxLoanTermMonths })}
                error={Boolean(loanFormErrors.termMonths)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Repayment Method")}
                select
                fullWidth
                value={loanForm.termType}
                onChange={(e) => setLoanForm({ ...loanForm, termType: e.target.value })}
              >
                <MenuItem value="DAILY">{t("Daily")}</MenuItem>
                <MenuItem value="WEEKLY">{t("Weekly")}</MenuItem>
                <MenuItem value="MONTHLY">{t("Monthly")}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateLoanDialog} disabled={isStartingLoanOtp}>{t("Cancel")}</Button>
          <Button onClick={handleCreateLoanSubmit} variant="contained" disabled={isStartingLoanOtp} sx={{ bgcolor: brand }}>
            {isStartingLoanOtp ? t("Sending OTP...") : t("Send OTP")}
          </Button>
        </DialogActions>
      </Dialog>

      <LoanOtpDialog
        open={Boolean(loanOtpState)}
        otpState={loanOtpState}
        error={loanOtpError}
        verifying={isVerifyingLoanOtp}
        resending={isResendingLoanOtp}
        onClose={handleCloseLoanOtpDialog}
        onVerify={handleVerifyLoanOtp}
        onResend={handleResendLoanOtp}
      />

      {/* Penalty Waiver Dialog */}
      <Dialog
        open={isWaiverDialogOpen}
        onClose={handleCloseWaiverDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: brand, fontWeight: 800 }}>
          {t("Request Penalty Waiver")}
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            {t(
              "Request a waiver for penalties during a specific period due to unforeseen circumstances. This request will be sent to leadership for approval."
            )}
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label={t("Reason for Waiver")}
                fullWidth
                multiline
                rows={3}
                value={waiverForm.reason}
                onChange={(e) =>
                  setWaiverForm({ ...waiverForm, reason: e.target.value })
                }
                helperText={t("e.g., Sickness, family emergency, etc.")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Waiver Start Date")}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().slice(0, 10) }}
                value={waiverForm.start}
                onChange={(e) =>
                  setWaiverForm({ ...waiverForm, start: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("Waiver End Date")}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: waiverForm.start
                    ? waiverForm.start
                    : new Date().toISOString().slice(0, 10),
                }}
                value={waiverForm.end}
                onChange={(e) =>
                  setWaiverForm({ ...waiverForm, end: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWaiverDialog}>{t("Cancel")}</Button>
          <Button onClick={handleWaiverSubmit} variant="contained" sx={{ bgcolor: brand }}>
            {t("Submit Request")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
